import { useCallback, useRef } from "react";
import { idempotencyKey } from "@/lib/idempotency";

/**
 * Chave de idempotência estável por **operação lógica** (hub#704).
 *
 * O ponto inteiro do header é o retry: se o usuário tenta de novo depois de uma falha de rede, o
 * backend precisa reconhecer que é a MESMA operação. Gerar a chave dentro do `mutationFn` daria
 * uma chave nova a cada clique — o mesmo que não mandar chave nenhuma.
 *
 * Por isso a chave nasce com o formulário e só é trocada quando a operação **conclui**: até lá,
 * toda tentativa é a mesma operação; depois, a próxima é outra.
 *
 * ```tsx
 * const { key, rotate } = useIdempotencyKey();
 * useMutation({
 *   mutationFn: (data) => inviteBroker(data.email, data.linkType, key()),
 *   onSuccess: () => { rotate(); ... },
 * });
 * ```
 */
export function useIdempotencyKey() {
  const ref = useRef<string | null>(null);

  const key = useCallback(() => {
    ref.current ??= idempotencyKey();
    return ref.current;
  }, []);

  /** Fecha a operação: a próxima tentativa passa a ser uma operação nova. */
  const rotate = useCallback(() => {
    ref.current = null;
  }, []);

  return { key, rotate };
}
